/**
 * Group drag across the two selection stores.
 *
 * Layout elements are selected into `selection.selectedIds`, equipment overlays
 * into `equipment.selectedInstanceIds`. A marquee fills both, so a drag started
 * on a member of either store has to carry the other store's members along by
 * the same delta. Each hook handles the *companions* — the items the dragged
 * node is not itself responsible for — so the existing per-store drag logic
 * (snapping, commit, history) stays untouched.
 */
import { useCallback, useMemo, useRef } from 'react';
import { useAppDispatch, useAppSelector } from './redux';
import { transformElements } from '../features/layout/layoutSlice';
import { addHistoryAction } from '../features/history/historySlice';
import { recordEquipmentHistory } from '../features/equipment/equipmentSlice';
import { translateSelectedInstances } from '../features/equipment/equipmentGroupDrag';
import { generateId } from '../utils/geometry';
import type { LayoutElement } from '../types';

/** Selected equipment overlays dragged alongside something else. */
export function useEquipmentDragCompanions() {
  const dispatch = useAppDispatch();
  const selectedInstanceIds = useAppSelector((s) => s.equipment.selectedInstanceIds);

  const dragRef = useRef<{
    exclude: string | null;
    applied: { dx: number; dy: number };
    snapshotTaken: boolean;
  } | null>(null);

  const begin = useCallback(
    (exclude?: string) => {
      const companions = selectedInstanceIds.filter((id) => id !== exclude);
      dragRef.current =
        companions.length > 0
          ? { exclude: exclude ?? null, applied: { dx: 0, dy: 0 }, snapshotTaken: false }
          : null;
    },
    [selectedInstanceIds]
  );

  /** Called with the running total delta; only the unapplied remainder moves. */
  const move = useCallback(
    (dx: number, dy: number) => {
      const drag = dragRef.current;
      if (!drag) return;
      const step = { dx: dx - drag.applied.dx, dy: dy - drag.applied.dy };
      if (step.dx === 0 && step.dy === 0) return;
      if (!drag.snapshotTaken) {
        // Snapshot before the first movement so one undo rewinds the whole drag.
        drag.snapshotTaken = true;
        dispatch(recordEquipmentHistory());
      }
      drag.applied = { dx, dy };
      dispatch(translateSelectedInstances(step, { exclude: drag.exclude }));
    },
    [dispatch]
  );

  /**
   * Returns true when companions moved under a history snapshot this hook owns —
   * the caller can then fold the dragged item into that same undo step instead of
   * committing (and snapshotting) it separately.
   */
  const end = useCallback(
    (dx: number, dy: number) => {
      const drag = dragRef.current;
      if (!drag) return false;
      move(dx, dy);
      const snapshotTaken = drag.snapshotTaken;
      dragRef.current = null;
      return snapshotTaken;
    },
    [move]
  );

  const cancel = useCallback(() => {
    dragRef.current = null;
  }, []);

  return {
    hasCompanions: selectedInstanceIds.length > 0,
    begin,
    move,
    end,
    cancel,
  };
}

/** Selected layout elements dragged alongside something else. */
export function useLayoutDragCompanions() {
  const dispatch = useAppDispatch();
  const elements = useAppSelector((s) => s.layout.elements);
  const selectedIds = useAppSelector((s) => s.selection.selectedIds);

  const movableIds = useMemo(
    () => selectedIds.filter((id) => elements[id] && !elements[id].metadata.locked),
    [elements, selectedIds]
  );

  const dragRef = useRef<{
    origins: Record<string, { x: number; y: number }>;
    snapshots: LayoutElement[];
    applied: { dx: number; dy: number };
  } | null>(null);

  const begin = useCallback(() => {
    if (movableIds.length === 0) {
      dragRef.current = null;
      return;
    }
    const origins: Record<string, { x: number; y: number }> = {};
    const snapshots: LayoutElement[] = [];
    for (const id of movableIds) {
      const element = elements[id];
      origins[id] = { x: element.position.x, y: element.position.y };
      snapshots.push({
        ...element,
        position: { ...element.position },
        dimensions: { ...element.dimensions },
        metadata: { ...element.metadata },
        customProperties: { ...(element.customProperties || {}) },
      });
    }
    dragRef.current = { origins, snapshots, applied: { dx: 0, dy: 0 } };
  }, [elements, movableIds]);

  const move = useCallback(
    (dx: number, dy: number) => {
      const drag = dragRef.current;
      if (!drag) return;
      if (drag.applied.dx === dx && drag.applied.dy === dy) return;
      drag.applied = { dx, dy };
      dispatch(
        transformElements(
          Object.entries(drag.origins).map(([id, origin]) => ({
            id,
            transform: { position: { x: origin.x + dx, y: origin.y + dy } },
          }))
        )
      );
    },
    [dispatch]
  );

  const end = useCallback(
    (dx: number, dy: number, timestamp: number) => {
      const drag = dragRef.current;
      if (!drag) return;
      move(dx, dy);
      dragRef.current = null;
      if (dx === 0 && dy === 0) return;
      const previousState = drag.snapshots;
      const newState = previousState.map((element) => ({
        ...element,
        position: { ...element.position, x: element.position.x + dx, y: element.position.y + dy },
      }));
      dispatch(
        addHistoryAction({
          id: generateId(),
          type: 'move',
          timestamp,
          elementIds: previousState.map((element) => element.id),
          previousState,
          newState,
        })
      );
    },
    [dispatch, move]
  );

  const cancel = useCallback(() => {
    dragRef.current = null;
  }, []);

  return {
    hasCompanions: movableIds.length > 0,
    begin,
    move,
    end,
    cancel,
  };
}
